import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { publicGet, mediaUrl } from "@/lib/api";
import {
  listenSiteCacheInvalidation,
  clearSiteSessionCaches,
  upsertProfilePreload,
} from "@/lib/siteCache";

const SiteContext = createContext(null);
const CACHE_KEY = "site_settings_v1";

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return m ? `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}` : "120, 119, 198";
};

export function applyTheme(theme) {
  if (!theme) return;
  const root = document.documentElement;
  if (theme.mode === "light") root.classList.add("theme-light");
  else root.classList.remove("theme-light");
  if (theme.primaryColor) root.style.setProperty("--primary", theme.primaryColor);
  if (theme.primaryColor) root.style.setProperty("--orb-1", `rgba(${hexToRgb(theme.primaryColor)}, 0.35)`);
  if (theme.blur != null) root.style.setProperty("--blur", `${theme.blur}px`);
  if (theme.radius != null) root.style.setProperty("--radius", `${theme.radius}px`);
  if (theme.shadow != null) root.style.setProperty("--shadow", `${theme.shadow}px`);
}

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function SiteProvider({ children }) {
  const cached = readCache();
  const [settings, setSettingsState] = useState(cached);
  const [loading, setLoading] = useState(!cached);

  const applySettings = useCallback((data) => {
    if (!data || typeof data !== "object") return;
    setSettingsState(data);
    writeCache(data);
    applyTheme(data.theme);
    const photo = data?.profile?.photo;
    if (photo) {
      const v = data.updated_at || data.profile?.updated_at;
      upsertProfilePreload(photo, (u) => mediaUrl(u, v));
    }
    try {
      localStorage.setItem("site_show_loader", data?.theme?.showLoader === false ? "0" : "1");
    } catch {
      /* ignore */
    }
  }, []);

  const setSettings = useCallback(
    (data) => {
      applySettings(data);
      setLoading(false);
    },
    [applySettings],
  );

  const load = useCallback(async () => {
    try {
      const { data } = await publicGet("/content/settings");
      applySettings(data);
    } catch {
      /* keep cache if any */
    }
    setLoading(false);
  }, [applySettings]);

  useEffect(() => {
    document.documentElement.dir = "rtl";
    document.documentElement.lang = "fa";
    if (cached?.theme) applyTheme(cached.theme);
    if (cached?.profile?.photo) {
      upsertProfilePreload(cached.profile.photo, (u) => mediaUrl(u, cached.updated_at));
    }
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isPay = path.startsWith("/pay");
    if (isPay) {
      // Defer settings fetch so payment page paints first
      setLoading(false);
      const t = setTimeout(load, 1200);
      return () => clearTimeout(t);
    }
    load();
    return undefined;
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return listenSiteCacheInvalidation(() => {
      clearSiteSessionCaches();
      load();
    });
  }, [load]);

  return (
    <SiteContext.Provider value={{ settings, setSettings, loading, reload: load }}>
      {children}
    </SiteContext.Provider>
  );
}

export const useSite = () => useContext(SiteContext);
